// NPM Imports
import moment from "moment";

// Components Imports

// Internal Imports
import { GRID } from "../../constants";

// Destructuring
const {
  CHARGE_TYPE_METHODS,
  CONVERSION_FACTORS,
  DEALTABLE_COLUMNS,
  PRICING_METHODS,
  RECURRENCE,
  RECURRING_PERIOD,
} = GRID;

/*
==========================================
        Business Logic Functions
==========================================
*/

/**
 * * INDEX
 * - calculateBasePrice
 * - calculateConversionFactors
 * - calculateEndDate
 * - calculateInterMediateValue
 * - checkIsDIYPricing
 * - convertCustomToRawRowData
 * - convertRawToCustomRowData
 * - isBillingFrequencyEditable
 * - isCellEditable
 * - isExtBasePriceEditable
 * - isPriceEditable
 * - isQtyEditable
 * - isSellingTermEditable
 * - isSkuQtyEditable
 * - preCalculateDiscountedPrice
 * - preCalculateEndDate
 * - preCalculateExtBasePrice
 * - preCalculateNetPrice
 * - preCalculateQty
 * - preCalculateSkuQty
 * - preCalculateStartDate
 * - updateBasePrice
 * - updateBundleQty
 * - updateDiscountAmount
 * - updateDiscountPrice
 * - updateEndDate
 * - updateExtBasePrice
 * - updateNetPrice
 * - updateQty
 */

// ======================================================================

export const calculateBasePrice = ({ readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const {
    row: { rowData = {}, rowMeta: { pricingData: { type = "" } = {} } } = {},
    intermediateValue,
  } = payload;

  const qty = rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]].cellData;
  const skuQty =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY]].cellData;

  switch (type) {
    case PRICING_METHODS.STEP_TIER_PRICING:
      return intermediateValue / skuQty;
    case PRICING_METHODS.NON_LINEAR:
      return 0;
    default:
      return intermediateValue / (qty * skuQty);
  }
};

// ======================================================================

export const calculateConversionFactors = ({ readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const { row: { rowData = {} } = {} } = payload;

  const chargeFrequency =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.CHARGE_FREQUENCY]].cellData;
  const chargeType =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.CHARGE_TYPE]].cellData;
  const recurringPeriod =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.RECURRING_PERIOD]].cellData;

  if (
    chargeType === CHARGE_TYPE_METHODS.RECURRING ||
    chargeType === CHARGE_TYPE_METHODS.RECURRING_USAGE
  ) {
    payload.conversionFactor =
      CONVERSION_FACTORS[RECURRENCE[chargeFrequency]][
        RECURRENCE[recurringPeriod]
      ] || 1;
  }
  return payload.conversionFactor;
};

// ======================================================================

export const calculateEndDate = ({ readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const { row: { rowData = {} } = {} } = payload;

  const sellingTerm =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]].cellData || 1;
  const startDate =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.START_DATE]].cellData;
  const recurringPeriod =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.RECURRING_PERIOD]].cellData;

  let endDate = "";

  switch (recurringPeriod) {
    case RECURRING_PERIOD.DAILY:
      endDate = moment(startDate).add(sellingTerm, "d").format();
      break;
    case RECURRING_PERIOD.WEEKLY:
      endDate = moment(startDate)
        .add(7 * sellingTerm, "d")
        .format();
      break;
    case RECURRING_PERIOD.MONTHLY:
      endDate = moment(startDate).add(sellingTerm, "M").format();
      break;
    case RECURRING_PERIOD.QUARTERLY:
      endDate = moment(startDate)
        .add(3 * sellingTerm, "M")
        .format();
      break;
    case RECURRING_PERIOD.HALF_YEARLY:
      endDate = moment(startDate)
        .add(6 * sellingTerm, "M")
        .format();
      break;
    case RECURRING_PERIOD.YEARLY:
      endDate = moment(startDate)
        .add(12 * sellingTerm, "M")
        .format();
      break;
    default:
      endDate = moment(startDate).format();
  }
  payload.endDate = endDate;
  return endDate;
};

// ======================================================================

export const calculateInterMediateValue = ({ data, readOnlyData, payload }) => {
  /**
   * * IMP::
   *  This function acts as a common logic between add product & update cell value functions
   * * FORMULA::
   * IV1 - Tier Calc
   * IV2 - (IV1 * q1 * q2) + BP(pb)
   * EBP - IV2 * [st * cf]
   * BP - IV2 / (q1 * [q2])
   */

  const {
    columnName,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { row: { rowData = {}, rowMeta = {} } = {} } = payload;
  const qty = rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]].cellData;
  const skuQty =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY]].cellData;

  let finalPrice = 0;
  let intermediateTempValue = 0; // Holds the first
  let price = null; // Stores the per unit price value from the range
  if ((data && data.price) || columnName) {
    const defaultQty =
      (data && (data.default_qty > 0 ? data.default_qty : 1)) || qty;
    const {
      basePrice = 0,
      bundles,
      range: priceRange = [],
      type,
      value,
    } = (data && data.price) || rowMeta.pricingData;
    const MAX_QTY = 1000000000000000000000;

    // To Handle empty value in last range object
    const range = JSON.parse(JSON.stringify(priceRange));
    if (range.length > 0 && !range[range.length - 1].max) {
      range[range.length - 1].max = MAX_QTY;
    }
    switch (type) {
      case PRICING_METHODS.TIER_PRICING:
        {
          let qtyCount = defaultQty; // Acts as a max counter value (i.e value gets incremented until max value reaches)
          let sum = 0; // Holds the final sum value
          range.every(item => {
            const correction = item.min === 0 ? 0 : 1; // since the range starts from 0 for the first range
            const items = item.max - item.min + correction;
            if (qtyCount >= items) {
              sum += items * item.value;
              qtyCount -= items;
              return true;
            }
            sum += qtyCount * item.value;
            return false;
          });
          intermediateTempValue = JSON.parse(sum.toFixed(2));
          finalPrice = intermediateTempValue * skuQty + basePrice;
        }
        break;
      case PRICING_METHODS.VOLUME_PRICING:
        [price] = range.filter(
          num => num.min <= defaultQty && num.max >= defaultQty,
        );
        intermediateTempValue = JSON.parse(
          (defaultQty * price.value).toFixed(2),
        );
        finalPrice = intermediateTempValue * skuQty + basePrice;
        break;
      case PRICING_METHODS.STEP_TIER_PRICING:
        [price] = range.filter(
          num => num.min <= defaultQty && num.max >= defaultQty,
        );
        intermediateTempValue = JSON.parse(price.value).toFixed(2);
        finalPrice = intermediateTempValue * skuQty + basePrice;
        break;
      case PRICING_METHODS.BUNDLE_PRICING:
        {
          const bundleSumValue = bundles.reduce(
            (sumValue, item) => sumValue + item.price * item.months,
            0,
          ); // (Price * Months) in a loop
          intermediateTempValue = JSON.parse(bundleSumValue);
          finalPrice = intermediateTempValue * skuQty + basePrice;
        }
        break;
      case PRICING_METHODS.NON_LINEAR:
        {
          let bundleSumValue = 0;
          bundles.forEach(bundle => {
            const { months, price: bundlePrice, multiplySku } = bundle;
            bundleSumValue += months * bundlePrice * (multiplySku ? skuQty : 1);
          });
          intermediateTempValue = JSON.parse(bundleSumValue);
          finalPrice = intermediateTempValue + basePrice;
        }
        break;
      case PRICING_METHODS.PERCENTAGE_OF_TOTAL:
        finalPrice = 0;
        break;
      default:
        intermediateTempValue = parseFloat(value) || 0;
        finalPrice = intermediateTempValue * qty * skuQty + basePrice;
    }
    payload.intermediateValue = finalPrice;
  }
  return finalPrice;
};

// ======================================================================

export const checkIsDIYPricing = ({ payload }) => {
  const { row: { rowMeta: { pricingData: { type = "" } = {} } } = {} } =
    payload;

  //* If DIY PRICING then the ripple effect should not happen
  if (type === PRICING_METHODS.DIY_PRICING) {
    payload.break = true;
  }
};

// ======================================================================

export const convertCustomToRawRowData = (rowData, apiRequiredFields) =>
  rowData.map(row => {
    const obj = {};
    if (row.rowMeta.id) obj.id = row.rowMeta.id;
    Object.keys(row.rowData).forEach(fld => {
      if (apiRequiredFields.includes(fld)) obj[fld] = row.rowData[fld].cellData;
    });
    return obj;
  });

// ======================================================================

export const convertRawToCustomRowData = (
  addedCatalogIds,
  fieldMapIndex,
  linkedTables,
  rawRowData,
  sourceField,
  sourceTable,
) => {
  const rowData = [];
  const bundleDataFld = fieldMapIndex[DEALTABLE_COLUMNS.BUNDLE_QUANTITY]; // FLDXXX holding the products inside the bundle [{}].
  const catalogProductId = fieldMapIndex[DEALTABLE_COLUMNS.PRICELIST_ITEM]; // FLDXXX holding the product id.
  rawRowData.forEach((item, index) => {
    const rowObj = {
      rowData: {},
      rowMeta: {
        id: item.rowId,
        index: item.colValues[catalogProductId] || index, // Holds the product id, here index is used for deltaRowDataMode
        pricingData: {},
      },
    };

    //* CONVERT API ROW JSON STRUCTURE INTO REVVGRID ROW STRUCTURE
    Object.keys(item.colValues).forEach(fld => {
      rowObj.rowData[fld] = { cellData: item.colValues[fld], cellMeta: {} };
    });

    //* DO LOOKUP ON LINKEDROWS AND CREATE ROW DATA AS REVVGRID JSON STRUCTURE
    if (linkedTables && linkedTables.length > 0) {
      linkedTables.forEach(tbl => {
        const linkedRows = item.linkedRows.find(row => row.tblId === tbl.tblId);
        if (linkedRows) {
          linkedRows.rows.forEach(row => {
            Object.keys(row.colValues).forEach(fld => {
              if (tbl.ids.includes(fld)) {
                rowObj.rowData[fld] = {
                  cellData: row.colValues[fld],
                  cellMeta: {},
                };
              }
            });
          });
        }
      });
    }

    //* FUNCTION TO UPDATE ROW_META
    /**
     * Price column holds OBJECT as data type instead of INTEGER, and this obj data is available inside
     * a linked record table. Hence the below method is used to pick out that particular FLDXXX key.
     */
    rowObj.rowMeta.pricingData =
      item.linkedRows &&
      item.linkedRows.find(column => column.tblId === sourceTable).rows[0]
        .colValues[sourceField];

    /**
     * In case of pricing method being bundle, the person efforts entered in dealtable by the user should be retained & should
     * not show what entered in pricing page. For that a hidden column is used to get the updated bunlde values.
     */
    if (
      rowObj.rowMeta.pricingData &&
      rowObj.rowMeta.pricingData.type === PRICING_METHODS.BUNDLE_PRICING &&
      rowObj.rowData[bundleDataFld]
    ) {
      rowObj.rowMeta.pricingData = rowObj.rowData[bundleDataFld].cellData;
    }

    //* UPDATE PRODUCT CATALOG ITEMS
    /** Products id's are pushed into an array, this will make the UI to display that the product is already added */
    const productId = item.colValues[catalogProductId];
    if (productId) addedCatalogIds.push(productId);

    rowData.push(rowObj);
  });

  return rowData;
};

// ======================================================================

export const isBillingFrequencyEditable = ({ fieldMapIndex, row }) =>
  row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.BILLING_FREQUENCY_EDITABLE]]
    .cellData || false;

// ======================================================================

export const isCellEditable = () => true;

// ======================================================================

export const isExtBasePriceEditable = ({ row }) =>
  row.rowMeta.pricingData.type === PRICING_METHODS.DIY_PRICING;

// ======================================================================

export const isPriceEditable = ({ row }) =>
  row.rowMeta.pricingData.type === PRICING_METHODS.DIY_PRICING;

// ======================================================================

export const isQtyEditable = ({ fieldMapIndex, row }) => {
  let isEditable = false;
  if (
    (row.rowMeta.pricingData.type && row.rowMeta.pricingData.value) ||
    row.rowMeta.pricingData.type === PRICING_METHODS.NON_LINEAR ||
    row.rowMeta.pricingData.type === PRICING_METHODS.DIY_PRICING ||
    row.rowMeta.pricingData.type === PRICING_METHODS.PERCENTAGE_OF_TOTAL ||
    (!row.rowMeta.pricingData.isAdhoc &&
      row.rowMeta.pricingData.type === PRICING_METHODS.BUNDLE_PRICING)
  ) {
    isEditable =
      row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY_EDITABLE]]
        .cellData || false;
  } else {
    isEditable = true;
  }
  return isEditable;
};

// ======================================================================

export const isSellingTermEditable = ({ fieldMapIndex, row }) =>
  row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.IS_SELLINGTERM_EDITABLE]]
    .cellData || false;

// ======================================================================

export const isSkuQtyEditable = ({ fieldMapIndex, row }) =>
  row.rowData[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY_EDITABLE]]
    .cellData || false;

// ======================================================================

export const preCalculateDiscountedPrice = ({ readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const { row: { rowData = {} } = {} } = payload;
  const extBasePrice =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]].cellData;
  return extBasePrice * (1 - 0 / 100);
};

// ======================================================================

export const preCalculateEndDate = ({ readOnlyData, payload }) =>
  calculateEndDate({ readOnlyData, payload });

// ======================================================================

export const preCalculateExtBasePrice = ({ data, readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const { row: { rowData = {} } = {}, conversionFactor } = payload;

  // In case of DIY Pricing
  if (data.price.type === PRICING_METHODS.DIY_PRICING) {
    return data.price.minPrice;
  }

  const sellingTerm =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]].cellData || 1;
  return payload.intermediateValue * sellingTerm * conversionFactor;
};

// ======================================================================

export const preCalculateNetPrice = ({ readOnlyData, payload }) => {
  const { fieldMapIndex } = readOnlyData.metaData;
  const { row: { rowData = {} } = {} } = payload;
  const extBasePrice =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]].cellData;
  return extBasePrice - 0;
};

// ======================================================================

export const preCalculateQty = ({ data, properties }) => {
  if (
    [
      PRICING_METHODS.TIER_PRICING,
      PRICING_METHODS.STEP_TIER_PRICING,
      PRICING_METHODS.VOLUME_PRICING,
    ].includes(data.price.type) ||
    (data.price.type === PRICING_METHODS.FLAT_PRICE && data.quantity_uom)
  ) {
    return data[properties.lookUp];
  }
  return properties.defaultValue;
};

// ======================================================================

export const preCalculateSkuQty = ({ data, properties }) => {
  if (
    [
      PRICING_METHODS.BUNDLE_PRICING,
      PRICING_METHODS.NON_LINEAR,
      PRICING_METHODS.PERCENTAGE_OF_TOTAL,
      PRICING_METHODS.DIY_PRICING,
    ].includes(data.price.type) ||
    (data.price.type === PRICING_METHODS.FLAT_PRICE && !data.quantity_uom)
  ) {
    return data[properties.lookUp];
  }
  return properties.defaultValue;
};

// ======================================================================

export const preCalculateStartDate = () => new Date().toISOString();

// ======================================================================

export const updateBasePrice = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const {
    row: { rowData = {}, rowMeta: { pricingData: { type = "" } = {} } } = {},
    rowData: rows,
    intermediateValue,
  } = payload;

  const qty = rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]].cellData;
  const skuQty =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY]].cellData;

  switch (type) {
    case PRICING_METHODS.STEP_TIER_PRICING:
      rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]].cellData =
        intermediateValue / skuQty;
      break;
    case PRICING_METHODS.NON_LINEAR:
      rows[rowIndex].rowData[
        fieldMapIndex[DEALTABLE_COLUMNS.PRICE]
      ].cellData = 0;
      break;
    default:
      rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]].cellData =
        intermediateValue / (qty * skuQty);
  }
};

// ======================================================================

export const updateBundleQty = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { row: { rowMeta: { pricingData = {} } } = {}, rowData: rows } =
    payload;

  if (pricingData.type === PRICING_METHODS.BUNDLE_PRICING) {
    rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.BUNDLE_QUANTITY]] =
      {};
    rows[rowIndex].rowData[
      fieldMapIndex[DEALTABLE_COLUMNS.BUNDLE_QUANTITY]
    ].cellData = pricingData;
  }
};

// ======================================================================

export const updateDiscountAmount = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { row: { rowData = {} } = {}, rowData: rows } = payload;

  const extBasePrice =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]].cellData;
  const discountPercentage =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]].cellData;

  rows[rowIndex].rowData[
    fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]
  ].cellData = (extBasePrice * discountPercentage) / 100;
};

// ======================================================================

export const updateDiscountPrice = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { row: { rowData = {} } = {}, rowData: rows } = payload;
  const extBasePrice =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]].cellData;
  const discountPercentage =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]].cellData;

  rows[rowIndex].rowData[
    fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]
  ].cellData = extBasePrice * (1 - discountPercentage / 100);
};

// ======================================================================

export const updateEndDate = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { rowData: rows } = payload;

  rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.END_DATE]].cellData =
    calculateEndDate({ readOnlyData, payload });
};

// ======================================================================

export const updateExtBasePrice = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const {
    row: { rowData = {} } = {},
    rowData: rows,
    conversionFactor,
  } = payload;

  const sellingTerm =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]].cellData || 1;

  rows[rowIndex].rowData[
    fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]
  ].cellData = payload.intermediateValue * sellingTerm * conversionFactor;
};

// ======================================================================

export const updateNetPrice = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const { row: { rowData = {} } = {}, rowData: rows } = payload;

  const addtionalDiscountPercentage =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]]
      .cellData;
  const discountedPrice =
    rowData[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]].cellData;

  rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]].cellData =
    discountedPrice - (discountedPrice * addtionalDiscountPercentage) / 100;
};

// ======================================================================

export const updateQty = ({ readOnlyData, payload }) => {
  const {
    rowIndex,
    metaData: { fieldMapIndex },
  } = readOnlyData;
  const {
    row: {
      rowMeta: { pricingData: { price = 0, qty = 0, type = "" } = {} },
    } = {},
    rowData: rows,
  } = payload;

  if (price || type === PRICING_METHODS.STEP_TIER_PRICING) {
    rows[rowIndex].rowData[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]].cellData =
      qty;
  }
};

// ======================================================================
